import { useCallback, useEffect, useRef, useState } from 'react'

export type LazyLoader<T> = {
	loaded: Map<number, T>
	loadingIds: Set<number>
	failedIds: Set<number>
	/** Loads once per id; concurrent calls share a request. `refresh` reloads a cached id. */
	load: (id: number, refresh?: boolean) => Promise<void>
	peek: (id: number) => T | undefined
	isPending: (id: number) => boolean
	cachedIds: () => number[]
	evict: (ids: number[]) => void
	clear: () => void
}

export function useLazyLoader<T>(fetch: (id: number) => Promise<T>): LazyLoader<T> {
	const [loaded, setLoaded] = useState<Map<number, T>>(new Map())
	const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())
	const [failedIds, setFailedIds] = useState<Set<number>>(new Set())
	const cache = useRef(new Map<number, T>())
	const requests = useRef(new Map<number, Promise<void>>())
	const fetchRef = useRef(fetch)
	fetchRef.current = fetch
	// Cleared entries must not be restored by requests that were already in flight.
	const generation = useRef(0)
	const mounted = useRef(true)

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	const load = useCallback(async (id: number, refresh = false) => {
		const currentRequest = requests.current.get(id)
		if (refresh && currentRequest) await currentRequest.catch(() => undefined)
		const pending = requests.current.get(id)
		if (pending) return pending
		if (!refresh && cache.current.has(id)) return
		if (!mounted.current) return
		const requestGeneration = generation.current
		setFailedIds((prev) => {
			const next = new Set(prev)
			next.delete(id)
			return next
		})
		setLoadingIds((prev) => new Set(prev).add(id))
		const request = fetchRef
			.current(id)
			.then((value) => {
				if (!mounted.current || requestGeneration !== generation.current) return
				cache.current.set(id, value)
				setLoaded(new Map(cache.current))
			})
			.catch((err) => {
				if (mounted.current && requestGeneration === generation.current)
					setFailedIds((prev) => new Set(prev).add(id))
				throw err
			})
			.finally(() => {
				if (requests.current.get(id) === request) requests.current.delete(id)
				if (!mounted.current) return
				setLoadingIds((prev) => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
			})
		requests.current.set(id, request)
		return request
	}, [])

	const peek = useCallback((id: number) => cache.current.get(id), [])
	const isPending = useCallback((id: number) => requests.current.has(id), [])
	const cachedIds = useCallback(() => Array.from(cache.current.keys()), [])

	const evict = useCallback((ids: number[]) => {
		if (!ids.some((id) => cache.current.has(id))) return
		ids.forEach((id) => cache.current.delete(id))
		setLoaded(new Map(cache.current))
	}, [])

	const clear = useCallback(() => {
		generation.current++
		cache.current.clear()
		requests.current.clear()
		setLoaded(new Map())
		setLoadingIds(new Set())
		setFailedIds(new Set())
	}, [])

	return { loaded, loadingIds, failedIds, load, peek, isPending, cachedIds, evict, clear }
}
